import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { CgSpinner } from 'react-icons/cg'
import { yupResolver } from '@hookform/resolvers/yup'
import { MdInfoOutline } from 'react-icons/md'

import {
  Badge,
  ButtonApp,
  Collapse,
  ConfirmDialog,
  FormApp,
  FormControl,
  InputApp,
  SelectField,
  SwitchField,
  TextAreaInput,
  ClipboardItem,
  MonitoringHintModal,
} from '@/components'
import { CognitoConfig, Store } from '@/models/store'
import { getBadgeVariantByStatus, getVersion, omit } from '@/utils'

import { backendResourcesSchema } from '../data/schema'
import { useArtifacts } from '../../groups/hooks'
import { BackendResourcesFormData } from '../data/types'
import { useUpdateStore } from '../../stores/hooks'
import { IdentityTypes, monitorConfigDefinitionItems, monitorConfigExample } from '../data/data'

const BackendResourcesForm: FC<{ store?: Store }> = ({ store }) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useForm<BackendResourcesFormData>({
    defaultValues: getBackendResourcesValuesFromStore(store),
    values: getBackendResourcesValuesFromStore(store),
    mode: 'onChange',
    resolver: yupResolver(backendResourcesSchema),
    disabled: !store,
  })
  const [dataToUpdate, setDataToUpdate] = useState<BackendResourcesFormData | undefined>(undefined)
  const { updateStore, isUpdating } = useUpdateStore({
    onSuccess: () => setDataToUpdate(undefined),
  })
  const isSESEnabled = watch().ses?.enabled ?? false
  const isDomainVerified = JSON.parse(store?.outputs ?? '{}')?.CognitoUserPoolGroup?.ses
    ?.sesDomainVerified
  const dkimRecords = JSON.parse(store?.outputs ?? '{}')?.CognitoUserPoolGroup?.ses?.dkimRecords

  const verifiedBadgeVariant = getBadgeVariantByStatus(
    isDomainVerified ? 'VERIFIED' : 'NOT_VERIFIED',
  )

  const { artifacts, isLoading: isLoadingArtifacts } = useArtifacts()

  const lambdaAlgoliaSrcKey =
    artifacts?.['e-com-dynamodbstream-srv']?.map((artifact) => ({
      label: artifact,
      value: `e-com-dynamodbstream-srv/${artifact}`,
    })) ?? []
  const cognitoLambdaTriggerSrcKey =
    artifacts?.['e-com-cognito-worker']?.map((artifact) => ({
      label: artifact,
      value: `e-com-cognito-worker/${artifact}`,
    })) ?? []

  const [showMonitoringHint, setShowMonitoringHint] = useState(false)

  const renderItemTemplate = (value: string) => (
    <span className="text-black rounded-full px-2 text-xs w-fit h-6 flex items-center bg-[#f5a66c]">
      {getVersion(value)}
    </span>
  )

  return (
    <>
      <FormApp
        handleCancel={() => {}}
        handleSubmit={handleSubmit}
        onSubmit={(data: BackendResourcesFormData) => {
          setDataToUpdate(data)
        }}
        defaultButtonsHidden
        outsideModal
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-800 font-medium">Backend Resources</span>
            <ButtonApp
              type="submit"
              width="md:min-w-[160px] w-fit"
              className="h-12 flex items-center gap-2 text-sm"
              isDisabled={['REQUESTED', 'IN_PROGRESS'].includes(store?.workflowStatus ?? '')}
            >
              {isUpdating && <CgSpinner className="animate-spin text-lg" />}
              <span>Deploy changes</span>
            </ButtonApp>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-6">
              <fieldset className="w-full rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
                <label>Backend compute</label>
                <div className="grid grid-cols-2 gap-6">
                  <FormControl label="Algolia">
                    <SelectField
                      name="lambdaAlgoliaSrcKey"
                      register={register}
                      options={lambdaAlgoliaSrcKey}
                      placeholder="Select version"
                      isLoading={isLoadingArtifacts}
                      control={control}
                      valueTemplate={renderItemTemplate}
                      itemTemplate={renderItemTemplate}
                      optionClassName="data-[focus]:bg-yellow-50 data-[selected]:bg-yellow-50 hover:bg-yellow-50"
                      checkIconClassName="text-yellow-100 group-data-[selected]:text-[#f5a66c]"
                    />
                  </FormControl>
                  <FormControl label="Cognito">
                    <SelectField
                      name="cognitoLambdaTriggerSrcKey"
                      register={register}
                      options={cognitoLambdaTriggerSrcKey}
                      placeholder="Select version"
                      isLoading={isLoadingArtifacts}
                      control={control}
                      valueTemplate={renderItemTemplate}
                      itemTemplate={renderItemTemplate}
                      optionClassName="data-[focus]:bg-yellow-50 data-[selected]:bg-yellow-50 hover:bg-yellow-50"
                      checkIconClassName="text-yellow-100 group-data-[selected]:text-[#f5a66c]"
                    />
                  </FormControl>
                </div>
              </fieldset>
              <fieldset className="w-full rounded-lg border border-[#F0F2F7] p-6 flex flex-col gap-6 self-start">
                <div className="flex justify-between items-center">
                  <label>Monitoring</label>
                  <ButtonApp
                    className="px-4 py-1.5 gap-2 h-8"
                    handleClick={() => {
                      setShowMonitoringHint(true)
                    }}
                  >
                    <MdInfoOutline className="text-xl text-gray-400" />
                    <span className="text-sm">Admin Hint</span>
                  </ButtonApp>
                </div>
                <FormControl messageError={errors.monitor?.message}>
                  <TextAreaInput
                    rows={7}
                    name="monitor"
                    placeholder="Enter monitoring config"
                    register={register}
                    isError={!!errors.monitor?.message}
                  />
                </FormControl>
              </fieldset>
            </div>
            <fieldset className="rounded-lg border border-[#F0F2F7] p-6 w-full gap-6 flex flex-col">
              <label>IDP</label>
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex justify-between">
                    <label className="text-sm">Email Config</label>
                    <SwitchField
                      name="ses.enabled"
                      control={control}
                      onChange={(checked) => {
                        if (!checked) trigger()
                      }}
                    />
                  </div>
                  <div />
                  <FormControl
                    label="Identity Type"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    messageError={(errors.ses?.identity?.type as any)?.message}
                  >
                    <SelectField
                      name="ses.identity.type"
                      register={register}
                      options={IdentityTypes}
                      placeholder="Select type"
                      isLoading={isLoadingArtifacts}
                      control={control}
                      isDisabled={!isSESEnabled}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      isError={!!(errors.ses?.identity?.type as any)?.message}
                    />
                  </FormControl>
                  <FormControl
                    label="Identity Value"
                    messageError={errors.ses?.identity?.value?.message}
                    customElement={
                      <Badge
                        className="w-fit text-xs uppercase"
                        title="Status"
                        {...verifiedBadgeVariant}
                      >
                        {isDomainVerified ? 'Verified' : 'Not Verified'}
                      </Badge>
                    }
                  >
                    <InputApp
                      name="ses.identity.value"
                      placeholder="Identity Value"
                      register={register}
                      isError={!!errors.ses?.identity?.value?.message}
                      isDisabled={!isSESEnabled}
                    />
                  </FormControl>
                  {!!dkimRecords?.length && (
                    <Collapse className="w-full col-span-2 @container">
                      {({ open }) => (
                        <>
                          <Collapse.Header className="justify-end items-center text-gray-400 text-sm">
                            <div className="flex gap-1 cursor-pointer items-start">{`${open ? 'Hide' : 'See'} DKIM Records`}</div>
                          </Collapse.Header>
                          <Collapse.Panel className="grid @xl:grid-cols-2 gap-6">
                            {dkimRecords.map((record: Record<string, string>, index: number) => (
                              <div key={index} className="flex flex-col gap-4">
                                <ClipboardItem headless value={JSON.stringify(record, null, 2)}>
                                  {({ onClick, clipboardIcon }) => (
                                    <div className="flex justify-between items-baseline">
                                      <div className="text-gray-400 break-all">DKIM Record</div>
                                      <div
                                        className="flex justify-end"
                                        onClick={onClick}
                                        role="button"
                                      >
                                        <span className="self-start">{clipboardIcon}</span>
                                      </div>
                                    </div>
                                  )}
                                </ClipboardItem>
                                <textarea
                                  className="border-[1px] rounded-[6px] p-[20px] text-secondary text-[14px] border-gray-200 focus:border-gray-300 outline-none resize-none"
                                  disabled
                                  rows={4}
                                  value={JSON.stringify(record, null, 2)}
                                />
                              </div>
                            ))}
                          </Collapse.Panel>
                        </>
                      )}
                    </Collapse>
                  )}
                  <FormControl label="Email From" messageError={errors.ses?.email?.from?.message}>
                    <InputApp
                      name="ses.email.from"
                      placeholder="John Doe <doe@gmail.com>"
                      register={register}
                      isError={!!errors.ses?.email?.from?.message}
                      isDisabled={!isSESEnabled}
                    />
                  </FormControl>
                  <FormControl
                    label="Email Reply To"
                    messageError={errors.ses?.email?.replyTo?.message}
                  >
                    <InputApp
                      name="ses.email.replyTo"
                      placeholder="doe@gmail.com"
                      register={register}
                      isError={!!errors.ses?.email?.replyTo?.message}
                      isDisabled={!isSESEnabled}
                    />
                  </FormControl>
                </div>
                <FormControl messageError={errors.cognitoConfig?.message}>
                  <TextAreaInput
                    rows={7}
                    name="cognitoConfig"
                    placeholder="Enter cognito config"
                    register={register}
                    isError={!!errors.cognitoConfig?.message}
                  />
                </FormControl>
              </div>
            </fieldset>
          </div>
        </div>
      </FormApp>
      <ConfirmDialog
        visible={!!dataToUpdate}
        confirmText="Are you sure yo want to deploy these changes?"
        onHide={() => setDataToUpdate(undefined)}
        onConfirm={() => updateStore(sanitizeFormData(dataToUpdate!))}
        isLoading={isUpdating}
      />
      <MonitoringHintModal
        monitorConfigDefinitionItems={monitorConfigDefinitionItems}
        monitorConfigExample={monitorConfigExample}
        isOpen={showMonitoringHint}
        handleCloseModal={() => setShowMonitoringHint(false)}
      />
    </>
  )
}

const getBackendResourcesValuesFromStore = (store?: Store): BackendResourcesFormData | undefined =>
  store
    ? {
        ...store,
        cognitoConfig: JSON.stringify(omit(store.cognitoConfig ?? {}, ['ses']), null, 2),
        monitor: JSON.stringify(store.monitor ?? {}, null, 2),
        ses: store.cognitoConfig?.ses ?? {
          enabled: false,
          identity: { type: '', value: '' },
          email: { from: '', replyTo: '' },
        },
      }
    : undefined

const sanitizeFormData = (data: BackendResourcesFormData): Store => {
  const parsedCognitoConfig = data?.cognitoConfig && JSON.parse(data?.cognitoConfig)
  const parsedMonitorConfig = data?.monitor ? JSON.parse(data?.monitor) : undefined
  const isSesEnabled = data?.ses?.enabled

  const cognitoConfig: CognitoConfig =
    parsedCognitoConfig || isSesEnabled
      ? { ...(parsedCognitoConfig ?? {}), ...(isSesEnabled ? { ses: data?.ses } : {}) }
      : undefined

  delete data.ses

  const store: Store = {
    ...data,
    cognitoConfig,
    monitor: parsedMonitorConfig,
  }
  return store
}

export { BackendResourcesForm }
